import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// Bottom sheet and toast, shared by every screen. One of each at a time, like WhatsApp.
// The sheet is drawn by <SheetHost />, placed inside the chats data, because sheets read it
// (Dr C, Reassign). Drawn here, outside it, they crashed the app to a blank screen (15 Oct).
//
// A message's menu opens in place instead, as WhatsApp (Gary, 16 Oct): the message lifted
// over a dimmed chat, its reactions above it and its options below.

export interface SheetPlace {
  /** The message the menu is for: it is shown lifted, with the menu beside it. */
  anchor?: HTMLElement | null;
  /** Drawn above the lifted message (the reactions). */
  top?: ReactNode;
  /** One of ours: the menu lines up on the right, under the message. */
  outgoing?: boolean;
}

interface OverlayValue {
  openSheet: (content: ReactNode, place?: SheetPlace) => void;
  closeSheet: () => void;
  toast: (text: string) => void;
  sheet: ReactNode;
  place: SheetPlace | null;
  open: boolean;
  /** Empties the sheet once it has slid away, so it does not go blank while closing. */
  clearSheet: () => void;
  openedAt: { current: number };
}

const OverlayContext = createContext<OverlayValue | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [sheet, setSheet] = useState<ReactNode>(null);
  const [place, setPlace] = useState<SheetPlace | null>(null);
  const [open, setOpen] = useState(false);
  const [toastText, setToastText] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  // When it opened: the finger that long-pressed to open a sheet lifts a moment later, and
  // that lift must not tap whatever option is under it (Gary, 15 Oct).
  const openedAt = useRef(0);
  // Android's back button closes an open sheet first, as in WhatsApp (Gary, 16 Oct). An open
  // sheet adds one step to history on the same page; back takes that step away.
  const openRef = useRef(false);
  // A back taken by closeSheet that has not landed yet: it must not close a sheet opened since.
  const backPending = useRef(false);
  const openSheet = useCallback((content: ReactNode, where?: SheetPlace) => {
    openedAt.current = Date.now();
    setSheet(content);
    setPlace(where?.anchor ? where : null);
    setOpen(true);
    openRef.current = true;
    const state = window.history.state as Record<string, unknown> | null;
    if (!state?.chatsSheet || backPending.current) window.history.pushState({ ...(state ?? {}), chatsSheet: true }, '');
  }, []);
  const closeSheet = useCallback(() => {
    setOpen(false);
    openRef.current = false;
    // Taken back after this tap has finished: if the tap went to another screen, that screen
    // stays, and the extra step is skipped the next time back lands on it (below).
    window.setTimeout(() => {
      // Not if another sheet opened straight after (it uses the same step).
      if (!openRef.current && (window.history.state as Record<string, unknown> | null)?.chatsSheet) {
        backPending.current = true;
        window.history.back();
      }
    }, 0);
  }, []);
  const clearSheet = useCallback(() => {
    if (openRef.current) return;
    setSheet(null);
    setPlace(null);
  }, []);

  useEffect(() => {
    const onBack = () => {
      if (backPending.current) { backPending.current = false; return; }
      const onSheetStep = !!(window.history.state as Record<string, unknown> | null)?.chatsSheet;
      if (openRef.current && !onSheetStep) {
        openRef.current = false;
        setOpen(false);
      } else if (!openRef.current && onSheetStep) {
        // A step left by a sheet that closed as the app moved on: step over it.
        window.history.back();
      }
    };
    window.addEventListener('popstate', onBack);
    return () => window.removeEventListener('popstate', onBack);
  }, []);

  // Moving to another screen closes the sheet (it is drawn over every screen).
  const { pathname } = useLocation();
  useEffect(() => {
    if (!openRef.current) return;
    openRef.current = false;
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on a change of screen
  }, [pathname]);
  const toast = useCallback((text: string) => {
    setToastText(text);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastText(null), 2400);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSheet(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeSheet]);

  return (
    <OverlayContext.Provider value={{ openSheet, closeSheet, clearSheet, toast, sheet, place, open, openedAt }}>
      {children}
      <div className={`chats-toast${toastText ? ' chats-toast--on' : ''}`} role="status">{toastText}</div>
    </OverlayContext.Provider>
  );
}

const CLOSE_DRAG = 80;
const EDGE = 10;

/** Draws the open bottom sheet, or a message's menu in place. Place it inside the chats data provider. */
export function SheetHost() {
  const { sheet, place, open, closeSheet, clearSheet, openedAt } = useOverlay();
  // Taps in the first moment after opening are the opening press lifting: ignore them.
  const settling = () => Date.now() - openedAt.current < 450;
  const swallow = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    if (settling()) { e.preventDefault(); e.stopPropagation(); }
  };
  const sheetRef = useRef<HTMLDivElement>(null);
  const popOpen = open && !!place;

  // Swipe the sheet down to close it, as on an iPhone (Gary, 16 Oct). Only from the top of
  // its content, so scrolling a long sheet still scrolls. The drag offset is the one inline
  // value: it follows the finger.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    let startY = 0;
    let startX = 0;
    let startAt = 0;
    let dy = 0;
    let tracking = false;
    let dragging = false;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      // Only when everything under the finger is scrolled to its top: a list or text box
      // inside the sheet that is scrolled down scrolls back up first.
      let node = e.target instanceof Element ? e.target : null;
      let atTop = true;
      while (node && node !== el) {
        if (node.scrollTop > 0) { atTop = false; break; }
        node = node.parentElement;
      }
      tracking = atTop && el.scrollTop <= 0;
      startX = e.touches[0]?.clientX ?? 0;
      dragging = false;
      dy = 0;
      startY = e.touches[0]?.clientY ?? 0;
      startAt = Date.now();
    };
    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const touch = e.touches[0];
      if (!touch) return;
      const d = touch.clientY - startY;
      if (!dragging) {
        if (d < -4) { tracking = false; return; }
        if (d < 8) {
          // A sideways swipe (a row of chips) is left to scroll sideways.
          if (Math.abs(touch.clientX - startX) > 8) tracking = false;
          return;
        }
        if (Math.abs(touch.clientX - startX) > d) { tracking = false; return; }
        dragging = true;
        el.classList.add('chats-sheet--drag');
      }
      dy = Math.max(0, d);
      e.preventDefault();
      el.style.transform = `translateY(${dy}px)`;
    };
    const onEnd = () => {
      if (!dragging) { tracking = false; return; }
      const fast = dy > 30 && dy / Math.max(1, Date.now() - startAt) > 0.5;
      el.classList.remove('chats-sheet--drag');
      el.style.transform = '';
      tracking = false;
      dragging = false;
      if (dy > CLOSE_DRAG || fast) closeSheet();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [closeSheet]);

  // A menu in place has no slide to wait for: it empties as it closes.
  useEffect(() => {
    if (!open && place) clearSheet();
  }, [open, place, clearSheet]);

  return (
    <>
      <div className={`chats-scrim${open && !place ? ' chats-scrim--on' : ''}`} onClick={(e) => { if (settling()) { swallow(e); return; } closeSheet(); }} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={`chats-sheet${open && !place ? ' chats-sheet--on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open || !!place}
        onClickCapture={swallow}
        onPointerUpCapture={swallow}
        onTransitionEnd={(e) => { if (e.target === e.currentTarget && !open) clearSheet(); }}
      >
        <div className="chats-sheet-grab" />
        {!place && sheet}
      </div>
      {popOpen && place?.anchor && (
        <MessagePop anchor={place.anchor} top={place.top} outgoing={!!place.outgoing} onClose={() => { if (!settling()) closeSheet(); }} swallow={swallow}>
          {sheet}
        </MessagePop>
      )}
    </>
  );
}

/**
 * A message's menu in place: a copy of the message where it sits, its reactions above it and
 * its options below, over a dimmed chat. If there is no room it moves up or down to fit the
 * screen, as WhatsApp does.
 */
function MessagePop({ anchor, top, outgoing, children, onClose, swallow }: {
  anchor: HTMLElement; top?: ReactNode; outgoing: boolean; children: ReactNode; onClose: () => void;
  swallow: (e: { preventDefault: () => void; stopPropagation: () => void }) => void;
}) {
  const stack = useRef<HTMLDivElement>(null);
  const bubble = useRef<HTMLDivElement>(null);

  // Placed where the message is, and placed again while the keyboard finishes going down
  // (it moves the chat). These are the inline values: they come from where the message is.
  useLayoutEffect(() => {
    const el = stack.current;
    const holder = bubble.current;
    if (!el || !holder) return;
    holder.replaceChildren(anchor.cloneNode(true));
    const placeIt = () => {
      const rect = anchor.getBoundingClientRect();
      holder.style.width = `${rect.width}px`;
      const vv = window.visualViewport;
      const viewTop = vv ? vv.offsetTop : 0;
      const viewH = vv ? vv.height : window.innerHeight;
      // The phone's status bar and home bar are kept clear (the pop's padding is the safe area).
      const pop = el.parentElement;
      const safeTop = pop ? parseFloat(getComputedStyle(pop).paddingTop) || 0 : 0;
      const safeBottom = pop ? parseFloat(getComputedStyle(pop).paddingBottom) || 0 : 0;
      const height = el.offsetHeight;
      const wanted = rect.top - holder.offsetTop;
      const highest = viewTop + EDGE + safeTop;
      const lowest = viewTop + viewH - height - EDGE - safeBottom;
      el.style.top = `${Math.round(Math.max(highest, Math.min(wanted, lowest)))}px`;
      const screenW = window.innerWidth;
      const width = el.offsetWidth;
      el.style.left = `${Math.round(Math.max(EDGE, Math.min(rect.left, screenW - width - EDGE)))}px`;
      el.classList.add('chats-pop-stack--placed');
    };
    placeIt();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', placeIt);
    vv?.addEventListener('scroll', placeIt);
    window.addEventListener('resize', placeIt);
    return () => {
      vv?.removeEventListener('resize', placeIt);
      vv?.removeEventListener('scroll', placeIt);
      window.removeEventListener('resize', placeIt);
    };
  }, [anchor]);

  return (
    <div className="chats-pop" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        ref={stack}
        className={`chats-pop-stack${outgoing ? ' chats-pop-stack--out' : ''}`}
        onClickCapture={swallow}
        onPointerUpCapture={swallow}
      >
        {/* Only the reactions and options keep a tap; a tap on the copy of the message or beside the
            options closes the menu (Gary, 19 Oct: on a long message nearly the whole screen ignored taps,
            and the app looked frozen). */}
        {top && <div className="chats-pop-top" onClick={(e) => e.stopPropagation()}>{top}</div>}
        <div ref={bubble} className="chats-pop-bubble" aria-hidden="true" />
        <div className="chats-pop-menu" onClick={(e) => e.stopPropagation()}>{children}</div>
      </div>
    </div>
  );
}

export function useOverlay(): OverlayValue {
  const value = useContext(OverlayContext);
  if (!value) throw new Error('useOverlay must be used inside OverlayProvider');
  return value;
}

/** One row in a bottom sheet: round icon, title and a line of explanation. */
export function SheetOption({ icon, title, sub, danger = false, onClick }: { icon: ReactNode; title: string; sub: string; danger?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`chats-opt${danger ? ' chats-opt--danger' : ''}`} onClick={onClick}>
      <span className="chats-opt-icon">{icon}</span>
      <span className="chats-opt-text">
        <b>{title}</b>
        {sub && <span>{sub}</span>}
      </span>
    </button>
  );
}
